import React, { FC, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import LoadingSpinner from '../components/LoadingSpinner';
import ScrollWrapper from '../common/ScrollWrapper';
import './CopiesList.scss';
import { LoanFormat, mapParsedLoanFormat } from '../../../models/LoanFormat';
import { Trans, useTranslation } from 'react-i18next';
import { mapDiscount } from '../common/translationMappers/discountMapper';
import FormatCurrency from '../utils/formatCurrency';
import { ProductMetaData } from '../../../models/Product';
import { Article } from '../../../models/Article';
import { CopiesListOut, LineItemCopyOut, LineItemKey } from '../../store/api';
import { invalidateLineItemCopies, useLineItemCopyDetails } from '../../shop/orders/useLineItemCopyDetails';
import { mapLineItemKey } from '../utils/lineItemKeyMapper';
import { useAuthentication } from '../../app-store/authentication/useAuthentication';

type Props = {
    readonly product: ProductMetaData & Article;
    readonly fetchFunc: (productId: string) => Promise<CopiesListOut>;
    readonly disabled?: boolean;
    readonly onRemove?: () => Promise<void>;
    readonly removeCopyHint?: string;
    readonly minCopyAmount?: number;
};

export const CopiesList: FC<Props> = ({
    product,
    fetchFunc,
    onRemove,
    disabled = false,
    minCopyAmount = 0,
    removeCopyHint
}) => {
    const { t } = useTranslation();
    const {
        spoc: { taxName }
    } = useAuthentication();
    const wrapperRef = useRef<HTMLDivElement>(null);

    const { loanFormat, price } = product;
    const lineItemKey: LineItemKey = useMemo(() => mapLineItemKey(product), [product]);

    const { copies, isLoading } = useLineItemCopyDetails({
        lineItemKey,
        fetchFunc
    });
    const [isRemoving, setIsRemoving] = useState(false);

    const handleRemove = async () => {
        if (!onRemove) {
            return;
        }

        setIsRemoving(true);
        try {
            await onRemove();
            invalidateLineItemCopies(lineItemKey).then();
        } finally {
            setIsRemoving(false);
        }
    };

    if (isLoading) {
        return (
            <div className="copies-list">
                <LoadingSpinner width={30} />
            </div>
        );
    }

    if (copies.length < 1) {
        return <div className="copies-list">{t('components.productDetails.noCopyInfoAvailable')}</div>;
    }

    return (
        <ScrollWrapper ref={wrapperRef} className="copies-list">
            <table>
                <thead>
                    <tr>
                        <td>&nbsp;</td>
                        <td>&nbsp;</td>
                        <td className="a-right strong txt-up">
                            {t('reserveReports.reserveReport.viewReport.yourPrice')}
                        </td>
                        <td>&nbsp;</td>
                    </tr>
                </thead>
                <tbody>
                    {copies.map((copy, idx) => (
                        <CopiesListItem
                            loanFormat={loanFormat}
                            key={copy.id}
                            copy={copy}
                            disabled={disabled || idx < minCopyAmount}
                            isRemoving={isRemoving}
                            onRemove={handleRemove}
                            removeCopyHint={removeCopyHint}
                        />
                    ))}
                </tbody>
                <tfoot>
                    <tr>
                        <td colSpan={3}>
                            <hr className="cold-smoky" style={{ margin: '10px 0', borderTop: '1px solid' }} />
                        </td>
                        <td>&nbsp;</td>
                    </tr>
                    <tr className="sum">
                        <td colSpan={3}>
                            <Trans
                                i18nKey="reserveReports.reserveReport.viewReport.totalExTax"
                                components={[<span className="txt-small" />]}
                                values={{
                                    tax: taxName ?? 'tax'
                                }}
                            />
                            &nbsp;
                            {t('numberFormat.numberWithCurrencySymbol', {
                                val: FormatCurrency.convertIntegerToCurrencyFormat(price?.netValue),
                                currencySymbol: FormatCurrency.mapCurrencyToSymbol(price?.currency)
                            })}
                        </td>
                        <td>&nbsp;</td>
                    </tr>
                </tfoot>
            </table>
        </ScrollWrapper>
    );
};

type CopiesListItemProps = {
    readonly loanFormat: LoanFormat;
    readonly copy: LineItemCopyOut;
    readonly removeCopyHint: string | undefined;
    readonly disabled: boolean;
    readonly isRemoving: boolean;
    readonly onRemove: (copyId: number) => Promise<void>;
};

const CopiesListItem: FC<CopiesListItemProps> = ({
    loanFormat,
    copy,
    disabled,
    onRemove,
    removeCopyHint,
    isRemoving
}) => {
    const { t } = useTranslation();
    const hasDiscount = copy.basePrice && copy.basePrice !== copy.netValue;
    const discountTypeKey = mapDiscount(copy.discountTypeKey);

    return (
        <tr>
            <td className="a-right">1</td>
            <td>
                <div>
                    <strong>
                        {t(`loanFormat.plain.${mapParsedLoanFormat(loanFormat).toLowerCase()}`, {
                            count: 1
                        })}
                    </strong>
                </div>
                {copy.bundleTitle && (
                    <div className="cold-grey">
                        <span>{copy.bundleTitle}</span>
                    </div>
                )}
                {copy.savedPercentage && copy.savedPercentage > 0 ? (
                    <div className="cold-grey">
                        <span>
                            {t(`reserveReports.reserveReport.discountType.${discountTypeKey}`, {
                                percentage: copy.savedPercentage / 100,
                                defaultValue: `-${copy.savedPercentage / 100}% ${copy.discountType}`
                            })}
                        </span>
                    </div>
                ) : null}
            </td>
            <td className="a-right">
                {hasDiscount && (
                    <span className="cold-grey crossed">
                        {t('numberFormat.numberWithCurrencySymbol', {
                            val: FormatCurrency.convertIntegerToCurrencyFormat(copy.basePrice),
                            currencySymbol: FormatCurrency.mapCurrencyToSymbol(copy.currency)
                        })}
                    </span>
                )}
                &nbsp;&nbsp;
                <span className={classNames(hasDiscount && 'cold-dark-red')}>
                    {t('numberFormat.numberWithCurrencySymbol', {
                        val: FormatCurrency.convertIntegerToCurrencyFormat(copy.netValue),
                        currencySymbol: FormatCurrency.mapCurrencyToSymbol(copy.currency)
                    })}
                </span>
            </td>
            <td className="actions">
                {!disabled ? (
                    !isRemoving ? (
                        <a onClick={() => onRemove(copy.id)} title={removeCopyHint}>
                            <i className="material-icons small">delete</i>
                        </a>
                    ) : (
                        <a>
                            <i className="material-icons small grey disabled">delete</i>
                        </a>
                    )
                ) : null}
            </td>
        </tr>
    );
};
